import { Alert, Button } from '@evaneos/b2b-ui/legacy';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { DossierId } from '../../../../resources/dossier/types';
import { removeInTripReview } from '../../../../resources/in-trip-review';
import { InTripReview } from '../../../../resources/in-trip-review/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { useAppDispatch } from '../../../../shared/redux';

import s from './AlertReview.module.css';

type Props = {
    inTripReview: InTripReview;
    dossierId: DossierId;
};

export function AlertInTripReview({ inTripReview: review, dossierId }: Readonly<Props>) {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const handleRemoveReviewClick = () => {
        dispatch(removeInTripReview(dossierId));
        trackEvent((ampli, defaultProperties) => {
            ampli.hideFeedbackClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    return (
        <Alert
            collapsible={true}
            type={review.isTravelerSatisfied ? 'information' : 'warning'}
            title={
                formatMessage(
                    {
                        id: review.isTravelerSatisfied
                            ? 'dossier.in_trip_review.title.positive'
                            : 'dossier.in_trip_review.title.negative',
                    },
                    {
                        createdAt:
                            review.createdAt &&
                            review.createdAt.toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            }),
                        span: (chunks: ReactNode) => <span className={s.inTrip}>{chunks}</span>,
                    },
                ) as unknown as string
            }
            showContentByDefault={false}
            data-testid="in-trip-review"
        >
            <p className={s.alertContent}>
                <strong>
                    {formatMessage(
                        { id: 'dossier.in_trip_review.satisfaction_score' },
                        {
                            score: review.score,
                        },
                    )}
                </strong>
            </p>
            <p>
                {review.travellerReview ??
                    formatMessage({
                        id: review.isTravelerSatisfied
                            ? 'dossier.in_trip_review.empty.positive'
                            : 'dossier.in_trip_review.empty.negative',
                    })}
            </p>
            <Button
                className={s.hideReviewButton}
                danger
                priority="secondary"
                onClick={handleRemoveReviewClick}
            >
                {formatMessage({ id: 'dossier.trip_reviews.button' })}
            </Button>
        </Alert>
    );
}
